import { Injectable } from "@angular/core";
import { DateRange } from "../../shared/date/date-range.model";
import { Client } from "../../client/client.model";
import { Quote } from "../quote.type";
import { LclQuote } from "../models/quote-lcl.model";
import { FclQuote } from "../models/quote-fcl.model";
import { FclTariff } from "../models/quote-fcl-tariff.model";
import { quoteStatus } from "./quote-status.const";
import { ManualQuote } from "../models/quote-manual.model";
import { QuoteMapper } from "../models/quote.mapper";
import { QuoteEmail } from "../models/email.model";
import { Package } from "./quote-package/package.model";
import { AirQuote } from "../models/quote-air.model";
import { LtlQuote } from "../models/quote-ltl.model";
import { LocalCostLabel } from "../models/local-cost-label.model";
import { format } from "date-fns";
import { containerNames } from "../../shared/container/container.const";
import { AirTariffQuote } from "../models/quote-airtariff.model";
import { MultiQuote } from "../models/quote-multi.model";
import { FtlQuote } from "../models/quote-ftl.model";
import { PtlQuote } from "../models/quote-ptl.model";

@Injectable({
  providedIn: "root",
})
export class QuoteUpdateService {
  constructor() {}

  public getPayloadFromQuoteAndPending(
    quote: Quote,
    pending: object | DateRange | Client | QuoteEmail,
  ): any {
    const payload = {};

    if (quote.getStatus() === quoteStatus.draft) {
      payload["discriminator"] = quote.getQuoteType();
    }

    if (Object.prototype.hasOwnProperty.call(pending, "clients")) {
      const clientIds = [];
      for (let i = 0; i < pending["clients"].length; i++) {
        clientIds.push(pending["clients"][i].getId());
      }
      payload["clients"] = clientIds;
    }

    if (Object.prototype.hasOwnProperty.call(pending, "insurance")) {
      payload["insurance"] = pending["insurance"];
    }

    if (Object.prototype.hasOwnProperty.call(pending, "ref")) {
      payload["ref"] = pending["ref"];
    }

    if (pending instanceof DateRange) {
      payload["validFrom"] = pending.getStartDateString();
      payload["validTo"] = pending.getEndDateString();
    }

    if (pending instanceof QuoteEmail) {
      payload["email"] = {};
      payload["email"]["to"] = pending.getTo();
      payload["email"]["cc"] = pending.getCc();
      payload["email"]["bcc"] = pending.getBcc();
      payload["email"]["subject"] = pending.getSubject();
      payload["email"]["message"] = pending.getMessage();
      payload["email"]["messageId"] = pending.getMessageId();
      payload["email"]["attachments"] = pending.getAttachments();
    }

    if (pending instanceof Object) {
      if (Object.prototype.hasOwnProperty.call(pending, "quoteType")) {
        payload["discriminator"] = pending["quoteType"];
      }

      if (Object.prototype.hasOwnProperty.call(pending, "validFrom")) {
        payload["validFrom"] = pending["validFrom"]
          ? format(new Date(pending["validFrom"]), "yyyy-MM-dd")
          : null;
      }

      if (Object.prototype.hasOwnProperty.call(pending, "validTo")) {
        payload["validTo"] = pending["validTo"]
          ? format(new Date(pending["validTo"]), "yyyy-MM-dd")
          : null;
      }

      if (Object.prototype.hasOwnProperty.call(pending, "intendedPickupDate")) {
        payload["intendedPickupDate"] = pending["intendedPickupDate"]
          ? format(new Date(pending["intendedPickupDate"]), "yyyy-MM-dd")
          : null;
      }

      if (
        Object.prototype.hasOwnProperty.call(pending, "intendedPickupDateTo")
      ) {
        payload["intendedPickupDateTo"] = pending["intendedPickupDateTo"]
          ? format(new Date(pending["intendedPickupDateTo"]), "yyyy-MM-dd")
          : null;
      }

      if (
        Object.prototype.hasOwnProperty.call(
          pending,
          "intendedDeliveryDateFrom",
        )
      ) {
        payload["intendedDeliveryDateFrom"] = pending[
          "intendedDeliveryDateFrom"
        ]
          ? format(new Date(pending["intendedDeliveryDateFrom"]), "yyyy-MM-dd")
          : null;
      }

      if (
        Object.prototype.hasOwnProperty.call(pending, "intendedDeliveryDateTo")
      ) {
        payload["intendedDeliveryDateTo"] = pending["intendedDeliveryDateTo"]
          ? format(new Date(pending["intendedDeliveryDateTo"]), "yyyy-MM-dd")
          : null;
      }

      if (
        Object.prototype.hasOwnProperty.call(pending, "intendedPickupTimeFrom")
      ) {
        payload["intendedPickupTimeFrom"] =
          pending["intendedPickupTimeFrom"] || null;
      }

      if (
        Object.prototype.hasOwnProperty.call(pending, "intendedPickupTimeTo")
      ) {
        payload["intendedPickupTimeTo"] =
          pending["intendedPickupTimeTo"] || null;
      }

      if (
        Object.prototype.hasOwnProperty.call(
          pending,
          "intendedDeliveryTimeFrom",
        )
      ) {
        payload["intendedDeliveryTimeFrom"] =
          pending["intendedDeliveryTimeFrom"] || null;
      }

      if (
        Object.prototype.hasOwnProperty.call(pending, "intendedDeliveryTimeTo")
      ) {
        payload["intendedDeliveryTimeTo"] =
          pending["intendedDeliveryTimeTo"] || null;
      }

      if (Object.prototype.hasOwnProperty.call(pending, "validFromTime")) {
        payload["validFromTime"] = pending["validFromTime"] || null;
      }

      if (Object.prototype.hasOwnProperty.call(pending, "validToTime")) {
        payload["validToTime"] = pending["validToTime"] || null;
      }

      if (Object.prototype.hasOwnProperty.call(pending, "customTerms")) {
        payload["customTerms"] = pending["customTerms"];
      }

      if (Object.prototype.hasOwnProperty.call(pending, "localCostLabels")) {
        const localCostLabels = pending["localCostLabels"] as LocalCostLabel[];
        payload["localCostLabels"] = localCostLabels.map((p) =>
          p.getRequestParams(),
        );
      }

      if (Object.prototype.hasOwnProperty.call(pending, "internalComment")) {
        payload["internalComment"] = pending["internalComment"];
      }

      if (Object.prototype.hasOwnProperty.call(pending, "ldm")) {
        payload["ldm"] = pending["ldm"];
      }

      if (Object.prototype.hasOwnProperty.call(pending, "direction")) {
        payload["direction"] = pending["direction"];
      }

      if (Object.prototype.hasOwnProperty.call(pending, "comment")) {
        payload["comment"] = pending["comment"];
      }

      if (Object.prototype.hasOwnProperty.call(pending, "equipment")) {
        payload["equipment"] = pending["equipment"];
      }

      if (Object.prototype.hasOwnProperty.call(pending, "serviceCodes")) {
        payload["serviceCodes"] = pending["serviceCodes"];
      }

      if (Object.prototype.hasOwnProperty.call(pending, "weightBrackets")) {
        payload["weightBrackets"] = pending["weightBrackets"];
      }

      if (Object.prototype.hasOwnProperty.call(pending, "incotermType")) {
        payload["incotermType"] = pending["incotermType"];
      }

      if (Object.prototype.hasOwnProperty.call(pending, "weight")) {
        payload["weight"] = pending["weight"];
      }

      if (Object.prototype.hasOwnProperty.call(pending, "measurement")) {
        payload["measurement"] = pending["measurement"];
      }

      if (Object.prototype.hasOwnProperty.call(pending, "pieces")) {
        payload["pieces"] = pending["pieces"];
      }

      if (Object.prototype.hasOwnProperty.call(pending, "packages")) {
        const packages = pending["packages"] as Package[];
        payload["packages"] = packages.map((p) => p.getRequestParams());
      }

      if (Object.prototype.hasOwnProperty.call(pending, "tier")) {
        payload["tier"] = pending["tier"]["id"];
      }

      if (Object.prototype.hasOwnProperty.call(pending, "originLocations")) {
        payload["originLocations"] = pending["originLocations"].map(
          (location) => location.getId(),
        );
      }

      if (
        Object.prototype.hasOwnProperty.call(pending, "destinationLocations")
      ) {
        payload["destinationLocations"] = pending["destinationLocations"].map(
          (location) => location.getId(),
        );
      }

      if (Object.prototype.hasOwnProperty.call(pending, "rates")) {
        payload["rates"] = pending["rates"].map((rate) =>
          rate["id"] ? rate["id"] : rate,
        );
      }

      if (Object.prototype.hasOwnProperty.call(pending, "manualQuote")) {
        if (pending["manualQuote"]["htmlContent"]) {
          payload["htmlContent"] = pending["manualQuote"]["htmlContent"] || "";
        }

        if (pending["manualQuote"]["form"]) {
          payload["netPrice"] = pending["manualQuote"]["form"]["netPrice"] || 0;
          payload["margin"] = pending["manualQuote"]["form"]["margin"] || 0;
        }

        if (pending["manualQuote"]["currency"]) {
          payload["currency"] = pending["manualQuote"]["currency"];
        }
      }

      if (
        quote instanceof FclQuote ||
        quote instanceof LclQuote ||
        quote instanceof AirQuote ||
        quote instanceof LtlQuote ||
        quote instanceof AirTariffQuote ||
        quote instanceof MultiQuote ||
        quote instanceof FtlQuote ||
        quote instanceof PtlQuote
      ) {
        if (Object.prototype.hasOwnProperty.call(pending, "offerLanguage")) {
          payload["offerLanguage"] = pending["offerLanguage"];
        }
        if (Object.prototype.hasOwnProperty.call(pending, "showCarrier")) {
          payload["showCarrier"] = pending["showCarrier"];
        }
      }

      if (
        quote instanceof FclQuote ||
        quote instanceof LclQuote ||
        quote instanceof AirQuote ||
        quote instanceof LtlQuote ||
        quote instanceof MultiQuote
      ) {
        if (Object.prototype.hasOwnProperty.call(pending, "showTotal")) {
          payload["showTotal"] = pending["showTotal"];
        }

        if (Object.prototype.hasOwnProperty.call(pending, "totalCurrency")) {
          payload["totalCurrency"] = pending["totalCurrency"];
        }

        if (Object.prototype.hasOwnProperty.call(pending, "showInland")) {
          payload["showInland"] = pending["showInland"];
        }
      }

      if (
        (quote instanceof FclQuote ||
          quote instanceof LclQuote ||
          quote instanceof AirQuote) &&
        Object.prototype.hasOwnProperty.call(
          pending,
          "showChargesInTotalCurrency",
        )
      ) {
        payload["showChargesInTotalCurrency"] =
          pending["showChargesInTotalCurrency"];
      }

      if (
        (quote instanceof FclQuote ||
          quote instanceof LclQuote ||
          quote instanceof AirQuote) &&
        Object.prototype.hasOwnProperty.call(pending, "chargesCurrency")
      ) {
        payload["chargesCurrency"] = pending["chargesCurrency"];
      }

      if (
        quote instanceof FclQuote ||
        quote instanceof LclQuote ||
        quote instanceof AirQuote ||
        quote instanceof LtlQuote ||
        quote instanceof FtlQuote ||
        quote instanceof PtlQuote
      ) {
        if (Object.prototype.hasOwnProperty.call(pending, "hasOrigin")) {
          payload["hasOrigin"] = pending["hasOrigin"];
        }

        if (Object.prototype.hasOwnProperty.call(pending, "hasDestination")) {
          payload["hasDestination"] = pending["hasDestination"];
        }

        if (Object.prototype.hasOwnProperty.call(pending, "groupRoutes")) {
          payload["groupRoutes"] = pending["groupRoutes"];
        }

        if (Object.prototype.hasOwnProperty.call(pending, "showInvoiceInfo")) {
          payload["showInvoiceInfo"] = pending["showInvoiceInfo"];
        }

        if (Object.prototype.hasOwnProperty.call(pending, "invoiceCurrency")) {
          payload["invoiceCurrency"] = pending["invoiceCurrency"];
        }

        if (
          Object.prototype.hasOwnProperty.call(pending, "groupOriginSurcharges")
        ) {
          payload["groupOriginSurcharges"] = pending["groupOriginSurcharges"];
        }

        if (
          Object.prototype.hasOwnProperty.call(
            pending,
            "groupDestinationSurcharges",
          )
        ) {
          payload["groupDestinationSurcharges"] =
            pending["groupDestinationSurcharges"];
        }

        if (
          Object.prototype.hasOwnProperty.call(
            pending,
            "groupOriginSurchargesCurrency",
          )
        ) {
          payload["groupOriginSurchargesCurrency"] =
            pending["groupOriginSurchargesCurrency"];
        }

        if (
          Object.prototype.hasOwnProperty.call(
            pending,
            "groupDestinationSurchargesCurrency",
          )
        ) {
          payload["groupDestinationSurchargesCurrency"] =
            pending["groupDestinationSurchargesCurrency"];
        }
      }

      if (
        quote instanceof FclQuote ||
        quote instanceof FclTariff ||
        quote instanceof LtlQuote
      ) {
        if (
          Object.prototype.hasOwnProperty.call(pending, "showRateSurcharges")
        ) {
          payload["showRateSurcharges"] = pending["showRateSurcharges"];
        }
      }

      if (quote instanceof FclQuote) {
        if (Object.prototype.hasOwnProperty.call(pending, "freightRateOrder")) {
          payload["freightRateOrder"] = pending["freightRateOrder"];
        }

        if (Object.prototype.hasOwnProperty.call(pending, "showGrandTotal")) {
          payload["showGrandTotal"] = pending["showGrandTotal"];
        }

        if (
          Object.prototype.hasOwnProperty.call(pending, "grandTotalCurrency")
        ) {
          payload["grandTotalCurrency"] = pending["grandTotalCurrency"];
        }

        if (
          Object.prototype.hasOwnProperty.call(pending, "equipmentQuantity") &&
          pending["equipmentQuantity"] instanceof Map
        ) {
          payload["equipmentQuantity"] = [];
          for (const containerName of containerNames) {
            if (pending["equipmentQuantity"].get(containerName)) {
              payload["equipmentQuantity"].push(
                pending["equipmentQuantity"].get(containerName).toArray(),
              );
            }
          }
        }
      }

      if (quote instanceof FclTariff) {
        if (
          Object.prototype.hasOwnProperty.call(pending, "showInternalNotes")
        ) {
          payload["showInternalNotes"] = pending["showInternalNotes"];
        }

        if (
          Object.prototype.hasOwnProperty.call(pending, "showExternalNotes")
        ) {
          payload["showExternalNotes"] = pending["showExternalNotes"];
        }

        if (Object.prototype.hasOwnProperty.call(pending, "showRateName")) {
          payload["showRateName"] = pending["showRateName"];
        }

        if (
          Object.prototype.hasOwnProperty.call(
            pending,
            "showAllLocationsInOneSheet",
          )
        ) {
          payload["showAllLocationsInOneSheet"] =
            pending["showAllLocationsInOneSheet"];
        }
      }

      if (quote instanceof LtlQuote) {
        if (
          Object.prototype.hasOwnProperty.call(pending, "intendedPickupDate")
        ) {
          payload["intendedPickupDate"] = pending["intendedPickupDate"]
            ? format(new Date(pending["intendedPickupDate"]), "yyyy-MM-dd")
            : null;
        }
        if (
          Object.prototype.hasOwnProperty.call(
            pending,
            "showNoMarginSurcharges",
          )
        ) {
          payload["showNoMarginSurcharges"] = pending["showNoMarginSurcharges"];
        }
      }

      if (quote instanceof PtlQuote) {
        if (
          Object.prototype.hasOwnProperty.call(pending, "intendedPickupDate")
        ) {
          payload["intendedPickupDate"] = pending["intendedPickupDate"]
            ? format(new Date(pending["intendedPickupDate"]), "yyyy-MM-dd")
            : null;
        }
      }

      if (quote instanceof FclQuote) {
        if (
          Object.prototype.hasOwnProperty.call(
            pending,
            "showServiceNameForLocalCharges",
          )
        ) {
          payload["showServiceNameForLocalCharges"] =
            pending["showServiceNameForLocalCharges"];
        }
        if (Object.prototype.hasOwnProperty.call(pending, "showRateName")) {
          payload["showRateName"] = pending["showRateName"];
        }
        if (Object.prototype.hasOwnProperty.call(pending, "showRateValidity")) {
          payload["showRateValidity"] = pending["showRateValidity"];
        }
      }

      if (quote instanceof LclQuote) {
        if (Object.prototype.hasOwnProperty.call(pending, "showRateValidity")) {
          payload["showRateValidity"] = pending["showRateValidity"];
        }
      }
    }

    if (quote instanceof MultiQuote) {
      if (Object.prototype.hasOwnProperty.call(pending, "summaryCurrency")) {
        payload["summaryCurrency"] = pending["summaryCurrency"];
      }
    }

    if (quote instanceof ManualQuote) {
      if (Object.prototype.hasOwnProperty.call(pending, "manualQuoteType")) {
        payload["manualQuoteType"] = pending["manualQuoteType"];
      }
    }

    return payload;
  }

  public getUpdatedQuoteFromPending(
    quote: Quote,
    pending: object | DateRange | Client,
  ) {
    const newQuote = QuoteMapper.map(quote);

    if (Object.prototype.hasOwnProperty.call(pending, "clients")) {
      newQuote.setClients(pending["clients"]);
    }

    if (Object.prototype.hasOwnProperty.call(pending, "insurance")) {
      newQuote.setInsurance(pending["insurance"]);
    }

    if (Object.prototype.hasOwnProperty.call(pending, "ref")) {
      newQuote.setRef(pending["ref"]);
    }

    if (pending instanceof DateRange) {
      newQuote.setValidFrom(pending.getStart());
      newQuote.setValidTo(pending.getEnd());
    }

    if (Object.prototype.hasOwnProperty.call(pending, "validFrom")) {
      newQuote.setValidFrom(pending["validFrom"]);
    }

    if (Object.prototype.hasOwnProperty.call(pending, "validTo")) {
      newQuote.setValidTo(pending["validTo"]);
    }

    if (
      newQuote instanceof FclQuote ||
      newQuote instanceof LclQuote ||
      newQuote instanceof AirQuote ||
      newQuote instanceof LtlQuote ||
      newQuote instanceof FclTariff ||
      newQuote instanceof FtlQuote ||
      newQuote instanceof MultiQuote
    ) {
      if (Object.prototype.hasOwnProperty.call(pending, "direction")) {
        newQuote.setDirection(pending["direction"]);
      }
    }

    if (
      newQuote instanceof FclQuote ||
      newQuote instanceof LclQuote ||
      newQuote instanceof AirQuote ||
      newQuote instanceof LtlQuote ||
      newQuote instanceof FclTariff ||
      newQuote instanceof FtlQuote ||
      newQuote instanceof MultiQuote ||
      newQuote instanceof PtlQuote ||
      newQuote instanceof AirTariffQuote
    ) {
      if (Object.prototype.hasOwnProperty.call(pending, "comment")) {
        newQuote.setComment(pending["comment"]);
      }
    }

    if (
      newQuote instanceof FclQuote ||
      newQuote instanceof FclTariff ||
      newQuote instanceof MultiQuote
    ) {
      if (Object.prototype.hasOwnProperty.call(pending, "equipment")) {
        newQuote.setEquipment(pending["equipment"]);
      }
    }

    if (newQuote instanceof AirTariffQuote) {
      if (Object.prototype.hasOwnProperty.call(pending, "weightBrackets")) {
        newQuote.setWeightBrackets(pending["weightBrackets"]);
      }
    }

    if (
      newQuote instanceof FclQuote ||
      newQuote instanceof FclTariff ||
      newQuote instanceof LtlQuote
    ) {
      if (Object.prototype.hasOwnProperty.call(pending, "showRateSurcharges")) {
        newQuote.setShowRateSurcharges(pending["showRateSurcharges"]);
      }
    }

    if (newQuote instanceof FclQuote) {
      if (Object.prototype.hasOwnProperty.call(pending, "freightRateOrder")) {
        newQuote.setFreightRateOrder(pending["freightRateOrder"]);
      }

      if (Object.prototype.hasOwnProperty.call(pending, "equipmentQuantity")) {
        newQuote.setEquipentQuantity(pending["equipmentQuantity"]);
      }

      if (Object.prototype.hasOwnProperty.call(pending, "showGrandTotal")) {
        newQuote.setShowGrandTotal(pending["showGrandTotal"]);
      }

      if (Object.prototype.hasOwnProperty.call(pending, "showRateName")) {
        newQuote.setShowRateName(pending["showRateName"]);
      }

      if (Object.prototype.hasOwnProperty.call(pending, "showRateValidity")) {
        newQuote.setShowRateValidity(pending["showRateValidity"]);
      }
    }

    if (newQuote instanceof LclQuote) {
      if (Object.prototype.hasOwnProperty.call(pending, "showRateValidity")) {
        newQuote.setShowRateValidity(pending["showRateValidity"]);
      }
    }

    if (
      newQuote instanceof FclQuote ||
      newQuote instanceof LclQuote ||
      newQuote instanceof AirQuote ||
      newQuote instanceof LtlQuote ||
      newQuote instanceof AirTariffQuote ||
      newQuote instanceof MultiQuote ||
      newQuote instanceof FtlQuote ||
      newQuote instanceof PtlQuote
    ) {
      if (Object.prototype.hasOwnProperty.call(pending, "offerLanguage")) {
        newQuote.setOfferLanguage(pending["offerLanguage"]);
      }
      if (Object.prototype.hasOwnProperty.call(pending, "showCarrier")) {
        newQuote.setShowCarrier(pending["showCarrier"]);
      }
    }

    if (
      newQuote instanceof FclQuote ||
      newQuote instanceof LclQuote ||
      newQuote instanceof AirQuote ||
      newQuote instanceof LtlQuote ||
      newQuote instanceof MultiQuote
    ) {
      if (Object.prototype.hasOwnProperty.call(pending, "showTotal")) {
        newQuote.setShowTotal(pending["showTotal"]);
      }

      if (Object.prototype.hasOwnProperty.call(pending, "totalCurrency")) {
        newQuote.setTotalCurrency(pending["totalCurrency"]);
      }

      if (Object.prototype.hasOwnProperty.call(pending, "showInland")) {
        newQuote.setShowInland(pending["showInland"]);
      }
    }

    if (
      (newQuote instanceof FclQuote ||
        newQuote instanceof LclQuote ||
        newQuote instanceof AirQuote) &&
      Object.prototype.hasOwnProperty.call(
        pending,
        "showChargesInTotalCurrency",
      )
    ) {
      newQuote.setShowChargesInTotalCurrency(
        pending["showChargesInTotalCurrency"],
      );
    }

    if (
      (newQuote instanceof FclQuote ||
        newQuote instanceof LclQuote ||
        newQuote instanceof AirQuote) &&
      Object.prototype.hasOwnProperty.call(pending, "chargesCurrency")
    ) {
      newQuote.setChargesCurrency(pending["chargesCurrency"]);
    }

    if (
      newQuote instanceof FclQuote ||
      newQuote instanceof LclQuote ||
      newQuote instanceof AirQuote ||
      newQuote instanceof LtlQuote
    ) {
      if (Object.prototype.hasOwnProperty.call(pending, "hasOrigin")) {
        newQuote.setHasOrigin(pending["hasOrigin"]);
      }

      if (Object.prototype.hasOwnProperty.call(pending, "hasDestination")) {
        newQuote.setHasDestination(pending["hasDestination"]);
      }

      if (Object.prototype.hasOwnProperty.call(pending, "localCostLabels")) {
        newQuote.setLocalCostLabels(pending["localCostLabels"]);
      }

      if (Object.prototype.hasOwnProperty.call(pending, "groupRoutes")) {
        newQuote.setGroupRoutes(pending["groupRoutes"]);
      }

      if (Object.prototype.hasOwnProperty.call(pending, "showInvoiceInfo")) {
        newQuote.setShowInvoiceInfo(pending["showInvoiceInfo"]);
      }

      if (Object.prototype.hasOwnProperty.call(pending, "invoiceCurrency")) {
        newQuote.setInvoiceCurrency(pending["invoiceCurrency"]);
      }

      if (Object.prototype.hasOwnProperty.call(pending, "incotermType")) {
        newQuote.setIncotermType(pending["incotermType"]);
      }

      if (
        Object.prototype.hasOwnProperty.call(pending, "groupOriginSurcharges")
      ) {
        newQuote.setGroupOriginSurcharges(pending["groupOriginSurcharges"]);
      }

      if (
        Object.prototype.hasOwnProperty.call(
          pending,
          "groupDestinationSurcharges",
        )
      ) {
        newQuote.setGroupDestinationSurcharges(
          pending["groupDestinationSurcharges"],
        );
      }

      if (
        Object.prototype.hasOwnProperty.call(
          pending,
          "groupOriginSurchargesCurrency",
        )
      ) {
        newQuote.setGroupOriginSurchargesCurrency(
          pending["groupOriginSurchargesCurrency"],
        );
      }

      if (
        Object.prototype.hasOwnProperty.call(
          pending,
          "groupDestinationSurchargesCurrency",
        )
      ) {
        newQuote.setGroupDestinationSurchargesCurrency(
          pending["groupDestinationSurchargesCurrency"],
        );
      }
    }

    if (
      newQuote instanceof LclQuote ||
      newQuote instanceof AirQuote ||
      newQuote instanceof LtlQuote ||
      newQuote instanceof FtlQuote ||
      newQuote instanceof PtlQuote ||
      newQuote instanceof MultiQuote
    ) {
      if (Object.prototype.hasOwnProperty.call(pending, "weight")) {
        newQuote.setWeight(pending["weight"]);
      }

      if (Object.prototype.hasOwnProperty.call(pending, "measurement")) {
        newQuote.setMeasurement(pending["measurement"]);
      }

      if (Object.prototype.hasOwnProperty.call(pending, "pieces")) {
        newQuote.setPieces(pending["pieces"]);
      }

      if (Object.prototype.hasOwnProperty.call(pending, "packages")) {
        newQuote.setPackages(pending["packages"]);
      }
      if (Object.prototype.hasOwnProperty.call(pending, "ldm")) {
        newQuote.setLdm(pending["ldm"]);
      }
    }

    if (newQuote instanceof AirQuote) {
      if (Object.prototype.hasOwnProperty.call(pending, "chargeableWeight")) {
        newQuote.setChargeableWeight(pending["chargeableWeight"]);
      }
      if (Object.prototype.hasOwnProperty.call(pending, "serviceCodes")) {
        newQuote.setServiceCodes(pending["serviceCodes"]);
      }
    }

    if (newQuote instanceof LtlQuote) {
      if (Object.prototype.hasOwnProperty.call(pending, "intendedPickupDate")) {
        newQuote.setIntendedPickupDate(pending["intendedPickupDate"]);
      }
      if (
        Object.prototype.hasOwnProperty.call(pending, "showNoMarginSurcharges")
      ) {
        newQuote.setShowNoMarginSurcharges(pending["showNoMarginSurcharges"]);
      }
    }

    if (newQuote instanceof PtlQuote) {
      if (Object.prototype.hasOwnProperty.call(pending, "intendedPickupDate")) {
        newQuote.setIntendedPickupDate(pending["intendedPickupDate"]);
      }
      if (
        Object.prototype.hasOwnProperty.call(pending, "intendedPickupDateTo")
      ) {
        newQuote.setIntendedPickupDateTo(pending["intendedPickupDateTo"]);
      }
      if (
        Object.prototype.hasOwnProperty.call(
          pending,
          "intendedDeliveryDateFrom",
        )
      ) {
        newQuote.setIntendedDeliveryDateFrom(
          pending["intendedDeliveryDateFrom"],
        );
      }
      if (
        Object.prototype.hasOwnProperty.call(pending, "intendedDeliveryDateTo")
      ) {
        newQuote.setIntendedDeliveryDateTo(pending["intendedDeliveryDateTo"]);
      }
      if (
        Object.prototype.hasOwnProperty.call(pending, "intendedPickupTimeFrom")
      ) {
        newQuote.setIntendedPickupTimeFrom(pending["intendedPickupTimeFrom"]);
      }
      if (
        Object.prototype.hasOwnProperty.call(pending, "intendedPickupTimeTo")
      ) {
        newQuote.setIntendedPickupTimeTo(pending["intendedPickupTimeTo"]);
      }
      if (
        Object.prototype.hasOwnProperty.call(
          pending,
          "intendedDeliveryTimeFrom",
        )
      ) {
        newQuote.setIntendedDeliveryTimeFrom(
          pending["intendedDeliveryTimeFrom"],
        );
      }
      if (
        Object.prototype.hasOwnProperty.call(pending, "intendedDeliveryTimeTo")
      ) {
        newQuote.setIntendedDeliveryTimeTo(pending["intendedDeliveryTimeTo"]);
      }
      if (Object.prototype.hasOwnProperty.call(pending, "validFromTime")) {
        newQuote.setValidFromTime(pending["validFromTime"]);
      }
      if (Object.prototype.hasOwnProperty.call(pending, "validToTime")) {
        newQuote.setValidToTime(pending["validToTime"]);
      }
    }

    if (newQuote instanceof FclTariff) {
      if (Object.prototype.hasOwnProperty.call(pending, "tier")) {
        newQuote.setTier(pending["tier"]);
      }

      if (Object.prototype.hasOwnProperty.call(pending, "showInternalNotes")) {
        newQuote.setShowInternalNotes(pending["showInternalNotes"]);
      }

      if (Object.prototype.hasOwnProperty.call(pending, "showExternalNotes")) {
        newQuote.setShowExternalNotes(pending["showExternalNotes"]);
      }

      if (Object.prototype.hasOwnProperty.call(pending, "showRateName")) {
        newQuote.setShowRateName(pending["showRateName"]);
      }

      if (
        Object.prototype.hasOwnProperty.call(
          pending,
          "showAllLocationsInOneSheet",
        )
      ) {
        newQuote.setShowAllLocationsInOneSheet(
          pending["showAllLocationsInOneSheet"],
        );
      }

      if (Object.prototype.hasOwnProperty.call(pending, "rates")) {
        newQuote.setRates(pending["rates"]);
      }
    }

    if (newQuote instanceof ManualQuote) {
      if (Object.prototype.hasOwnProperty.call(pending, "manualQuoteType")) {
        newQuote.setManualQuoteType(pending["manualQuoteType"]);
      }

      if (Object.prototype.hasOwnProperty.call(pending, "manualQuote")) {
        if (pending["manualQuote"]["htmlContent"]) {
          newQuote.setHtmlContent(pending["manualQuote"]["htmlContent"]);
        }

        if (pending["manualQuote"]["currency"]) {
          newQuote.setCurrency(pending["manualQuote"]["currency"]);
        }
      }
    }

    if (newQuote instanceof MultiQuote) {
      if (Object.prototype.hasOwnProperty.call(pending, "summaryCurrency")) {
        newQuote.setSummaryCurrency(pending["summaryCurrency"]);
      }
    }

    return newQuote;
  }
}
